import type { NilDealResponse } from '@on3/api';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import clsx from 'clsx';
import { AgentIcon } from 'components/Icons/AgentIcon';
import { SportIcon } from 'components/Icons/SportIcon';
import { UndecidedIcon } from 'components/Icons/UndecidedIcon';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { ArrowRight } from 'components/Svg/ArrowRight';
import { AthleteVerified } from 'components/UI/AthleteVerified/AthleteVerified';
import { TextLink } from 'components/UI/Link/TextLink';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';

import {
  divisionAbbreviation,
  getAbbreviatedRank,
  getNILProfileLink,
} from '../../helpers';
import styles from './PlayerDealItem.module.scss';

interface IDealResponse {
  item?: NilDealResponse;
}

export const PlayerDealItem = ({ item }: IDealResponse) => {
  const { rating, rosterRating, rpm, person, agent, nilValue, status } =
    item || {};
  const hasRpm = !!rpm?.length;
  const hasAgentNameAndCompany = agent?.name && agent.company;
  const hasCommittedAsset = !!status?.committedAsset;
  const hasTransferredAsset = !!status?.transferredAsset;
  const sportName = person?.defaultSport?.name || rating?.sport?.name;
  const primarySport = sportName === 'Football' || sportName === 'Basketball';

  const statusTeam = status?.committedAsset || status?.transferredAsset;
  const ratingTeamLogo = {
    src: statusTeam?.assetUrl || 'https://on3static.com/teams/default.svg',
    altText: statusTeam?.name || 'rating logo',
  };

  const inCollege = !person?.division?.includes('High');
  const profileLink = getNILProfileLink({
    sport: sportName,
    slug: person?.slug,
  });

  return (
    <div className={styles.playerWrapper}>
      <div className={styles.associationContainer}>
        {person?.division && (
          <Text className={styles.association} variant="subtitle1">
            {divisionAbbreviation(person?.division)}
          </Text>
        )}
        <SportIcon sportName={sportName} />
        <Text className={styles.classRank} variant="subtitle1">
          {person?.classRank
            ? getAbbreviatedRank(person?.classRank)
            : person?.classYear}
        </Text>
      </div>

      <div className={styles.playerContainer}>
        <TextLink href={profileLink} underline="none">
          <On3Avatar
            altText={person?.defaultAsset?.title}
            className={styles.playerAvatar}
            domain={person?.defaultAsset?.domain}
            height={60}
            radiusFive
            source={person?.defaultAsset?.source}
            title={person?.defaultAsset?.title ?? ''}
            width={60}
          />
        </TextLink>
        <div className={styles.playerDetailsContainer}>
          <div className={styles.playerName}>
            <TextLink href={profileLink} underline="none" variant="h5">
              {person?.fullName}
            </TextLink>
            {person?.athleteVerified && <AthleteVerified />}
          </div>
          <div className={styles.playerVitalsContainer}>
            <span aria-label="Position">{person?.position?.abbr ?? '-'}</span>
            <span role="separator">/</span>
            <span aria-label="Height">{person?.height ?? '-'}</span>
            {primarySport && (
              <>
                <span role="separator">/</span>
                <span aria-label="Weight">{person?.weight ?? '-'}</span>
              </>
            )}
          </div>
          <Text className={styles.hometownWrapper} variant="caption">
            <TextLink
              className={styles.highSchool}
              href={`/high-school/${person?.highSchool?.urlSlug}/`}
            >
              {person?.highSchool?.name}
            </TextLink>
            <span className={styles.hometown}>
              {person?.hometown?.abbr ?? '-'}
            </span>
          </Text>
          {(agent?.company || agent?.name) && (
            <div className={styles.agentContainer}>
              <AgentIcon className={styles.agentIcon} />
              <Text className={styles.agent}>
                {agent.name && (
                  <span
                    className={clsx(styles.agentName, {
                      [styles.addSlash]: hasAgentNameAndCompany,
                    })}
                  >
                    {agent?.name}
                  </span>
                )}
                {agent.company && <span>{agent?.company}</span>}
              </Text>
            </div>
          )}
        </div>
      </div>
      <div className={styles.statusContainer}>
        <div className={styles.assetsWrapper}>
          {hasTransferredAsset && (
            <div className={styles.transferredAssetContainer}>
              <TeamAvatar
                className={styles.transferredAssetLogo}
                height={25}
                slug={status?.transferredAsset?.slug}
                src={status?.transferredAsset?.assetUrl}
                width={25}
              />
              {hasCommittedAsset && <ArrowRight className={styles.arrow} />}
            </div>
          )}
          {hasCommittedAsset && (
            <div className={styles.committedAssetContainer}>
              <TeamAvatar
                className={styles.committedAssetLogo}
                height={25}
                slug={status?.committedAsset?.slug}
                src={status?.committedAsset?.assetUrl}
                width={25}
              />
              {status?.type === 'Committed' && (
                <Text className={styles.statusText} variant="subtitle1">
                  Hard Commit
                </Text>
              )}
            </div>
          )}
        </div>
        {!hasCommittedAsset && !hasTransferredAsset && !hasRpm && (
          <div className={styles.undecidedContainer}>
            <UndecidedIcon className={styles.undecidedIcon} />
            <Text variant="subtitle1">Undecided</Text>
          </div>
        )}
        {hasRpm && !hasCommittedAsset && !hasTransferredAsset && (
          <TextLink
            className={styles.predictionsContainer}
            href={`/db/${person?.slug}/recruiting/${person?.recruitmentKey}/rpm/`}
            underline="none"
          >
            {rpm?.slice(0, 2).map((org) => {
              if (org.organization?.slug) {
                return (
                  <div className={styles.prediction} key={org.organization.key}>
                    {org.percent && (
                      <Text className={styles.rpmText}>
                        {Math.round(org.percent)}%
                      </Text>
                    )}

                    <TeamAvatar
                      className={styles.predictionLogo}
                      height={30}
                      slug={org?.organization?.slug}
                      src={org?.organization.assetUrl}
                      width={30}
                    />
                  </div>
                );
              }

              return null;
            })}
          </TextLink>
        )}
        <div className={styles.nilValueContainer}>
          <Text className={styles.nilValue} variant="subtitle1">
            <span className={styles.label}>NIL Value</span>
            {nilValue ? (
              <span className={styles.nilValueText}>
                {formatCurrency(nilValue)}
              </span>
            ) : (
              <span className={styles.valuationUndisclosed}>undisclosed</span>
            )}
          </Text>
        </div>
      </div>
      {primarySport && (
        <div className={styles.ratingWrapper}>
          {inCollege && (
            <StarRating
              fiveStarPlus={rosterRating?.fiveStarPlus}
              href={`/db/${person?.slug}/industry-comparison/`}
              isBoldRating={true}
              isConsensus
              rating={rosterRating?.rating}
              rounding={2}
              stars={rosterRating?.stars}
              teamLogo={ratingTeamLogo}
              year={`${rosterRating?.year}`}
            />
          )}
          <StarRating
            grayRating
            href={`/db/${person?.slug}/industry-comparison/`}
            isBoldRating={false}
            isConsensus
            rating={rating?.consensusRating}
            small
            stars={rating?.consensusStars}
          />
        </div>
      )}
    </div>
  );
};
